import {
    endOfDay,
    endOfMonth,
    endOfWeek,
    endOfYear,
    format,
    formatISO,
    startOfDay,
    startOfMonth,
    startOfWeek,
    startOfYear,
} from 'date-fns'
import { string } from 'yup'

import imagePlaceholder from '../assets/img/placeholder.png'
import { ApiError, ResponseType } from '@/services/types'
import { FieldPath, FieldValues, UseFormSetError } from 'react-hook-form'

export const returnOnlyNumbers = (value: string) => {
    return value.replace(/\D/g, '')
}

export const getFullUrl = (path: string): string => {
    const baseUrl = process.env.NEXT_PUBLIC_API_URL
    return `${baseUrl}${path}`
}

export const getMediaUrl = (
    path: string | null | undefined,
    placeholder?: string,
): string => {
    const baseUrl = import.meta.env.VITE_API_URL

    if (!path) {
        return placeholder || imagePlaceholder
    }

    return `${baseUrl}${path}`
}

export const handleImageError = (
    e: React.SyntheticEvent<HTMLImageElement, Event>,
    placeholder: string = imagePlaceholder,
): void => {
    e.currentTarget.src = placeholder
}

export function getStartAndEndOfDay(date: Date): {
    startDate: string
    endDate: string
} {
    const start = startOfDay(date)
    const end = endOfDay(date)
    const formattedStart = formatISO(start)
    const formattedEnd = formatISO(end)

    return { startDate: formattedStart, endDate: formattedEnd }
}

export function getStartAndEndOfWeek(date: Date): {
    startDate: string
    endDate: string
} {
    const start = startOfWeek(date, { weekStartsOn: 1 }) // Начало недели (по умолчанию неделя начинается с воскресенья)
    const end = endOfWeek(date, { weekStartsOn: 1 }) // Конец недели
    const formattedStart = formatISO(start)
    const formattedEnd = formatISO(end)

    return { startDate: formattedStart, endDate: formattedEnd }
}
export function getStartAndEndOfMonth(date: Date): {
    startDate: string
    endDate: string
} {
    const start = startOfMonth(date) // Начало недели (по умолчанию неделя начинается с воскресенья)
    const end = endOfMonth(date) // Конец недели
    const formattedStart = formatISO(start)
    const formattedEnd = formatISO(end)

    return { startDate: formattedStart, endDate: formattedEnd }
}
export function getStartAndEndOfYear(date: Date): {
    startDate: string
    endDate: string
} {
    const start = startOfYear(date) // Начало недели (по умолчанию неделя начинается с воскресенья)
    const end = endOfYear(date) // Конец недели
    const formattedStart = formatISO(start)
    const formattedEnd = formatISO(end)

    return { startDate: formattedStart, endDate: formattedEnd }
}

export const forwardGeocode = async (address: string) => {
    try {
        const accessToken = import.meta.env.VITE_MAPBOX_ACCESS_TOKEN

        if (!accessToken) {
            console.error(
                'Mapbox Access Token is missing in environment variables.',
            )
            return null
        }

        const response = await fetch(
            `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(
                address,
            )}.json?access_token=${accessToken}`,
        )
        const data = await response.json()
        if (data.features.length > 0) {
            const [lng, lat] = data.features[0].center
            return { lng, lat }
        } else {
            return null
        }
    } catch (error) {
        return null
    }
}
export const objectToQueryString = (params: Record<string, any>): string => {
    return Object.entries(params)
        .filter(([_, value]) => value !== undefined && value !== null)
        .map(([key, value]) =>
            Array.isArray(value)
                ? `${key}=${value.join(',')}`
                : `${key}=${encodeURIComponent(value)}`,
        )
        .join('&')
}

export const setApiError = <TFieldValues extends FieldValues>(
    errors: ApiError[] | undefined,
    setError: UseFormSetError<TFieldValues>,
) => {
    errors?.forEach(({ field, msg }) => {
        setError(field as FieldPath<TFieldValues>, {
            type: 'server',
            message: msg,
        })
    })
}
